/**
 * Pure geometry and shaping for the graphical 16 Oct sections of the
 * Leadership report (Rusty, 19 Oct: "I want it all graphical"): the top
 * offerings, the bubble sizes and incident slices on the site map, the team
 * flow (a two-column Sankey), the bar scales and the request ages.
 *
 * Import-free, so `tests/leadership-viz.test.mjs` can load it directly.
 */

// ── Scales ──────────────────────────────────────────────────────────────────

/** A round axis top at or above `value`: 1, 2, 2.5, 5 or 10 × 10^k (at least 1). */
export function niceMax(value: number): number {
  if (!(value > 0)) {
    return 1;
  }
  const power = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 2.5, 5, 10]) {
    if (step * power >= value) {
      return step * power;
    }
  }
  return 10 * power;
}

/** `value` as a share of `max` in percent, clamped to 0–100 (0 when `max` is 0). */
export function widthPct(value: number, max: number): number {
  if (!(max > 0) || !(value > 0)) {
    return 0;
  }
  return Math.min(100, (100 * value) / max);
}

// ── What we're getting: the top offerings ───────────────────────────────────

export interface OfferingLike {
  offering: string;
  week: number;
  last_week: number;
  launch: number;
}

/**
 * The offerings the chart shows: busiest this week first, then last week,
 * then since launch, then by name; at most `n`. Offerings with nothing in
 * either week are left out (they are in "Show details").
 */
export function topOfferings<T extends OfferingLike>(rows: readonly T[], n = 10): T[] {
  return rows
    .filter((r) => r.week > 0 || r.last_week > 0)
    .slice()
    .sort(
      (a, b) =>
        b.week - a.week ||
        b.last_week - a.last_week ||
        b.launch - a.launch ||
        a.offering.localeCompare(b.offering),
    )
    .slice(0, n);
}

/** The age bucket (0–3) of a request, as the report's age colours: <1, 1–3, 3–7, >7 days. */
export function ageBucket(days: number | null | undefined): 0 | 1 | 2 | 3 {
  if (days === null || days === undefined || days < 1) {
    return 0;
  }
  if (days < 3) {
    return 1;
  }
  return days < 7 ? 2 : 3;
}

// ── Sites: bubbles on the map ───────────────────────────────────────────────

/** Bubble radius on a sqrt scale, so the *area* tracks the count (0 for nothing). */
export function bubbleRadius(
  value: number,
  max: number,
  range: [number, number] = [5, 26],
): number {
  if (!(value > 0) || !(max > 0)) {
    return 0;
  }
  const [lo, hi] = range;
  return lo + (hi - lo) * Math.sqrt(Math.min(value, max) / max);
}

/**
 * The SVG path of a pie wedge from 12 o'clock, clockwise, for `fraction` of
 * the circle; null for nothing or for a whole circle (draw a circle instead).
 */
export function wedgePath(cx: number, cy: number, r: number, fraction: number): string | null {
  if (!(fraction > 0) || fraction >= 1 || !(r > 0)) {
    return null;
  }
  const angle = fraction * 2 * Math.PI;
  const x = cx + r * Math.sin(angle);
  const y = cy - r * Math.cos(angle);
  const large = fraction > 0.5 ? 1 : 0;
  const f = (v: number) => v.toFixed(2);
  return `M${f(cx)},${f(cy)} L${f(cx)},${f(cy - r)} A${f(r)},${f(r)} 0 ${large} 1 ${f(x)},${f(y)} Z`;
}

export interface Located {
  site: string;
  lat?: number | null;
  lon?: number | null;
}

/** Sites with coordinates (on the map) and without (named under it: "Default", unknown codes). */
export function splitLocated<T extends Located>(
  rows: readonly T[],
): { mapped: T[]; unmapped: T[] } {
  const mapped: T[] = [];
  const unmapped: T[] = [];
  for (const r of rows) {
    if (typeof r.lat === "number" && typeof r.lon === "number") {
      mapped.push(r);
    } else {
      unmapped.push(r);
    }
  }
  return { mapped, unmapped };
}

/** The Europe close-up: Iberia to Scandinavia, as `[[west, south], [east, north]]`. */
export const EUROPE_BOX: [[number, number], [number, number]] = [
  [-10, 36],
  [24, 60],
];
/** The whole world of the sites: Mexico to Japan. */
export const WORLD_BOX: [[number, number], [number, number]] = [
  [-120, 5],
  [145, 62],
];

/** Whether a point lies inside a `[[west, south], [east, north]]` box. */
export function inBox(
  lon: number,
  lat: number,
  box: [[number, number], [number, number]],
): boolean {
  const [[w, s], [e, n]] = box;
  return lon >= w && lon <= e && lat >= s && lat <= n;
}

// ── Requesters ──────────────────────────────────────────────────────────────

/** A requester bucket key as the chart writes it: "2-3" → "2–3" (en dash). */
export function bucketLabel(key: string): string {
  return key.replace(/-/g, "–");
}

// ── Reassignment trail: the team flow ───────────────────────────────────────

export interface FlowPath {
  from_team: string;
  to_team: string;
  moves: number;
}

export interface FlowNode {
  /** `from:<team>` or `to:<team>`: a team can be on both sides. */
  id: string;
  side: "from" | "to";
  team: string;
  value: number;
  y: number;
  h: number;
}

export interface FlowLink {
  from: string;
  to: string;
  value: number;
  /** The band's filled outline. */
  d: string;
  /** Where the band leaves and arrives (its middle), for hit-testing and labels. */
  sy: number;
  ty: number;
  w: number;
}

export interface FlowLayout {
  nodes: FlowNode[];
  links: FlowLink[];
  height: number;
}

/**
 * A two-column Sankey of team moves: teams that moved tickets on the left,
 * teams that received them on the right, band widths ∝ moves. Nodes sort by
 * volume (then name); bands leave and arrive in the order of the other side,
 * so they cross as little as a two-column layout can.
 */
export function flowLayout(
  paths: readonly FlowPath[],
  opts: { x0: number; x1: number; height: number; gap?: number; top?: number; minBand?: number },
): FlowLayout {
  const gap = opts.gap ?? 8;
  const top = opts.top ?? 0;
  const minBand = opts.minBand ?? 1.5;
  const live = paths.filter((p) => p.moves > 0);
  const total = live.reduce((sum, p) => sum + p.moves, 0);
  if (!total) {
    return { nodes: [], links: [], height: opts.height };
  }
  const sums = (side: "from" | "to") => {
    const m = new Map<string, number>();
    for (const p of live) {
      const team = side === "from" ? p.from_team : p.to_team;
      m.set(team, (m.get(team) ?? 0) + p.moves);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  };
  const left = sums("from");
  const right = sums("to");
  const most = Math.max(left.length, right.length);
  const k = Math.max(0.0001, (opts.height - gap * (most - 1)) / total);
  const place = (side: "from" | "to", list: [string, number][]): FlowNode[] => {
    let y = top;
    return list.map(([team, value]) => {
      const h = Math.max(minBand, value * k);
      const node: FlowNode = { id: `${side}:${team}`, side, team, value, y, h };
      y += h + gap;
      return node;
    });
  };
  const nodes = [...place("from", left), ...place("to", right)];
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const rank = (side: "from" | "to", team: string) => byId.get(`${side}:${team}`)?.y ?? 0;
  const outY = new Map<string, number>();
  const inY = new Map<string, number>();
  const ordered = [...live].sort(
    (a, b) =>
      rank("from", a.from_team) - rank("from", b.from_team) ||
      rank("to", a.to_team) - rank("to", b.to_team),
  );
  // Bands leave each left node in the order of their right node, and arrive
  // at each right node in the order of their left node.
  const leaving = ordered;
  const arriving = [...ordered].sort(
    (a, b) =>
      rank("to", a.to_team) - rank("to", b.to_team) ||
      rank("from", a.from_team) - rank("from", b.from_team),
  );
  const starts = new Map<FlowPath, number>();
  const ends = new Map<FlowPath, number>();
  for (const p of leaving) {
    const node = byId.get(`from:${p.from_team}`)!;
    const y = outY.get(node.id) ?? node.y;
    starts.set(p, y);
    outY.set(node.id, y + Math.max(minBand, p.moves * k));
  }
  for (const p of arriving) {
    const node = byId.get(`to:${p.to_team}`)!;
    const y = inY.get(node.id) ?? node.y;
    ends.set(p, y);
    inY.set(node.id, y + Math.max(minBand, p.moves * k));
  }
  const { x0, x1 } = opts;
  const xm = (x0 + x1) / 2;
  const f = (v: number) => v.toFixed(1);
  const links: FlowLink[] = ordered.map((p) => {
    const w = Math.max(minBand, p.moves * k);
    const s = starts.get(p)!;
    const t = ends.get(p)!;
    const d =
      `M${f(x0)},${f(s)} C${f(xm)},${f(s)} ${f(xm)},${f(t)} ${f(x1)},${f(t)} ` +
      `L${f(x1)},${f(t + w)} C${f(xm)},${f(t + w)} ${f(xm)},${f(s + w)} ${f(x0)},${f(s + w)} Z`;
    return {
      from: p.from_team,
      to: p.to_team,
      value: p.moves,
      d,
      sy: s + w / 2,
      ty: t + w / 2,
      w,
    };
  });
  const bottom = Math.max(...nodes.map((n) => n.y + n.h));
  return { nodes, links, height: Math.max(opts.height, bottom) };
}

// ── Reassignment trail: time at a team ──────────────────────────────────────

export interface DwellLike {
  median_hours?: number | null;
  avg_hours?: number | null;
  p25_hours?: number | null;
  p75_hours?: number | null;
}

/** The hours axis top for the time-at-team chart (the largest of median, average, 75th pct). */
export function dwellMax(rows: readonly DwellLike[]): number {
  let max = 0;
  for (const r of rows) {
    for (const v of [r.median_hours, r.avg_hours, r.p75_hours]) {
      if (typeof v === "number" && v > max) {
        max = v;
      }
    }
  }
  return niceMax(max);
}

/**
 * The size key's reference values: a round number about a quarter of the
 * biggest site, then the biggest site itself. A key, not a site: the caption
 * beside it says what the sizes count.
 */
export function sizeKey(max: number): number[] {
  const quarter = max / 4;
  if (quarter < 2) {
    return [max];
  }
  const step = 10 ** Math.floor(Math.log10(quarter));
  const round = [1, 2, 5, 10].map((m) => m * step).filter((v) => v <= quarter);
  const ref = round[round.length - 1] ?? step;
  return ref < max ? [ref, max] : [max];
}
